import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import ResourceForm from './ResourceForm';
import ResourceRead from './ResourceRead';
import './ResourceMng.css';

import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const API_URL = '/api/resources';

/**
 * ✅ 컴포넌트 밖으로 빼낸 buildTree 함수
 * - useMemo 의존성 경고 제거
 * - 항상 동일한 함수 참조 유지
 */
const buildTree = (list, parentId = 0) => {
  return list
    .filter(item => item.parent_id === parentId)
    .sort((a, b) => {
      if (a.res_type !== b.res_type) {
        // 자원을 먼저, 카테고리를 뒤로
        return a.res_type === '자원' ? -1 : 1;
      }
      return (a.sort_order || 0) - (b.sort_order || 0);
    })
    .map(item => ({
      ...item,
      children: buildTree(list, item.id)
    }));
};

function ResourceMng() {
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [mode, setMode] = useState('view');
  const [expandedItems, setExpandedItems] = useState(['root']);
  const token = sessionStorage.getItem('token');

  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.id;
  const userRole = user?.role;

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const res = await axios.get(API_URL, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : res.data.data;
        setResources(data || []);
      } catch (err) {
        console.error('자원 목록 불러오기 실패:', err);
        setResources([]);
      }
    };
    fetchResources();
  }, [token]);

  // ✅ useMemo에서 buildTree 사용 → 경고 없음
  const treeData = useMemo(() => buildTree(resources, 0), [resources]);

  const handleSelect = (resource) => {
    setSelectedResource(resource);
    setMode('view');
  };

  const handleCreate = () => {
    setSelectedResource(null);
    setMode('create');
  };

  const handleSuccess = (savedResource) => {
    setResources(prev => {
      const exists = prev.some(r => r.id === savedResource.id);
      if (exists) {
        return prev.map(r => r.id === savedResource.id ? savedResource : r);
      } else {
        return [...prev, savedResource];
      }
    });
    setSelectedResource(savedResource);
    setMode('view');
  };

  const handleDelete = async (resource) => {
    const confirmed = window.confirm("정말 삭제하시겠습니까?");
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/${resource.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(resources.filter(r => r.id !== resource.id));
      setSelectedResource(null);
      setMode('view');
    } catch (err) {
      console.error("삭제 실패:", err);
    }
  };

  const getAllIds = (nodes) => {
    const ids = [];
    const walk = (arr) => {
      arr.forEach((n) => {
        ids.push(String(n.id));
        if (n.children) walk(n.children);
      });
    };
    walk(nodes);
    return ids;
  };

  useEffect(() => {
    if (resources.length > 0) {
      const allIds = ['root', ...getAllIds(buildTree(resources, 0))];
      setExpandedItems(allIds);
    }
  }, [resources]);

  // ✅ 자원은 항상 색 유지, 선택은 상위 div 배경색으로만 표현
  const renderTree = (nodes, depth = 1) => (
    nodes.map(node => {
      const hasChildren = node.children && node.children.length > 0;
      const isSelected = selectedResource?.id === node.id;

      return (
        <TreeItem
          key={node.id}
          itemId={String(node.id)}
          selected={isSelected}
          sx={{
            pl: `${depth * 10}px`,
            '& .MuiTreeItem-label': {
              fontSize: node.res_type === '카테고리' ? '12px' : '11px',
              fontWeight: node.res_type === '카테고리' ? 'bold' : 'normal'
            }
          }}
          collapseIcon={hasChildren ? <ExpandMoreIcon /> : <span className="dummy-icon" />}
          expandIcon={hasChildren ? <ChevronRightIcon /> : <span className="dummy-icon" />}
          label={
            <div
              className={node.res_type === '카테고리' ? "category-label" : "resource-box"}
              style={
                node.res_type === '자원'
                  ? { backgroundColor: node.res_color, color: '#fff' } // ✅ 항상 고유 색 유지
                  : {}
              }
              title={node.res_name}
              onClick={(e) => {
                if (node.res_type === '카테고리') {
                  e.stopPropagation();
                  handleSelect(node);
                } else {
                  handleSelect(node);
                }
              }}
            >
              {node.res_type === '카테고리' ? `📂 ${node.res_name}` : node.res_name}
            </div>
          }
        >
          {hasChildren && renderTree(node.children, depth + 1)}
        </TreeItem>
      );
    })
  );

  return (
    <div className="d-flex">
      {/* 좌측 트리 */}
      <div className="resource-sidebar border-end p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <button className="btn btn-primary btn-sm" onClick={handleCreate}>
            ➕ 자원생성
          </button>
          <div>
            <button
              className="expand-btn"
              onClick={() => setExpandedItems(['root'])}
            >
              −
            </button>
            <button
              className="expand-btn"
              onClick={() => setExpandedItems(['root', ...getAllIds(treeData)])}
            >
              +
            </button>
          </div>
        </div>
        <div className="tree-divider" />
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
          selectedItems={selectedResource ? [String(selectedResource.id)] : []}
          onSelectedItemsChange={(event, itemIds) => {
            const found = resources.find(r => String(r.id) === itemIds[0]);
            if (found) handleSelect(found);
          }}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
        >
          <TreeItem
            itemId="root"
            sx={{
              '& .MuiTreeItem-label': {
                fontSize: '15px',
                fontWeight: 'bold'
              }
            }}
            label="📂 예약자원"
          >
            {renderTree(treeData, 1)}
          </TreeItem>
        </SimpleTreeView>
      </div>

      {/* 우측 상세/폼 */}
      <div className="flex-grow-1 p-3">
        {mode === 'view' && selectedResource && (
          <ResourceRead
            resource={selectedResource}
            role={userRole}
            currentUserId={userId}
            onEdit={() => setMode('edit')}
            onDelete={(res) => handleDelete(res)}
          />
        )}

        {mode === 'create' && (
          <ResourceForm
            mode="create"
            onSuccess={handleSuccess}
            onCancel={() => setMode('view')}
            token={token}
          />
        )}

        {mode === 'edit' && selectedResource && (
          <ResourceForm
            mode="edit"
            resource={selectedResource}
            onSuccess={handleSuccess}
            onCancel={() => setMode('view')}
            token={token}
          />
        )}
      </div>
    </div>
  );
}

export default ResourceMng;
